// Streak reward tiers (coins awarded per streak day)
export const STREAK_REWARDS = {
  1: 5,
  2: 10,
  3: 15,
  4: 20,
  5: 25,
  6: 30,
  7: 50,
  default: 50, // Day 8+ maintains 50 coins
};

// Avatar mood constants
export const AVATAR_MOODS = {
  BROKEN: 'broken',
  NEUTRAL: 'neutral',
  SLIGHT_SMILE: 'slight_smile',
  HAPPY: 'happy',
  VERY_HAPPY: 'very_happy',
};

// Get mood expression mappings (eyebrows + mouth combinations)
export const getMoodExpression = (mood) => {
  const expressions = {
    broken: { eyebrows: 'suspicious', mouth: null },
    neutral: { eyebrows: null, mouth: null },
    slight_smile: { eyebrows: 'happy', mouth: null },
    happy: { eyebrows: 'happy', mouth: 'teeth' },
    very_happy: { eyebrows: 'curly', mouth: 'tongue' },
  };
  return expressions[mood] || expressions.neutral;
};

// Calculate current mood based on streak data
export const calculateMood = (streakData) => {
  if (!streakData) return AVATAR_MOODS.NEUTRAL;

  // Check if streak was recently broken (within 24 hours)
  if (streakData.streakBrokenAt) {
    const brokenTime = new Date(streakData.streakBrokenAt);
    const now = new Date();
    const hoursSinceBroken = (now - brokenTime) / (1000 * 60 * 60);
    if (hoursSinceBroken < 24) {
      return AVATAR_MOODS.BROKEN;
    }
  }

  const streak = streakData.currentStreak || 0;

  if (streak === 0) return AVATAR_MOODS.NEUTRAL;
  if (streak <= 3) return AVATAR_MOODS.SLIGHT_SMILE;
  if (streak <= 6) return AVATAR_MOODS.HAPPY;
  return AVATAR_MOODS.VERY_HAPPY;
};

// Get streak bonus coins for current streak day
export const getStreakBonus = (streakDay) => {
  return STREAK_REWARDS[streakDay] || STREAK_REWARDS.default;
};

// Pick a random item from an array
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

// Sassy avatar greetings based on stats, streak and time of day
// Every state has exactly 3 messages for variety.
export const getAvatarGreeting = (username, avatarStats, streakCount) => {
  const hour = new Date().getHours();
  const name = username || 'Doctor';
  const streak = streakCount || 0;

  const energy    = avatarStats?.energy    ?? 80;
  const happiness = avatarStats?.happiness ?? 80;
  const fullness  = avatarStats?.fullness  ?? 80;
  const avg = (energy + happiness + fullness) / 3;

  // Crisis — everything is critically low
  if (avg < 15) return pick([
    `${name}, I am NOT okay right now.`,
    `I'm filing a complaint with the medical board.`,
    `This is what neglect looks like. I hope you're satisfied.`,
  ]);

  // Find which stat is lowest
  const minVal = Math.min(energy, happiness, fullness);
  const lowestStat =
    minVal === fullness ? 'fullness' :
    minVal === energy   ? 'energy' : 'happiness';

  // Hungry — fullness is the problem
  if (lowestStat === 'fullness' && fullness < 50) return pick([
    `I can't think about nephrons right now. I need food.`,
    `Feed me and I'll tell you what the renal corpuscle does.`,
    `Studying on an empty stomach? Bold strategy, ${name}.`,
  ]);

  // Tired — energy is the problem (split by time of day)
  if (lowestStat === 'energy' && energy < 50) {
    if (hour < 10) return pick([
      `I cannot do this before coffee. I physically cannot.`,
      `Good morning. I use that term very loosely.`,
      `Is it too early to go back to sleep? Asking for me.`,
    ]);
    if (hour > 21) return pick([
      `I'm running on vibes and spite at this point.`,
      `Late night studying? Questionable, but I respect the chaos.`,
      `My energy left the building hours ago. I'm basically a ghost.`,
    ]);
    return pick([
      `My energy is on the floor. Someone pick it up.`,
      `I need a nap, a snack, and a miracle. In that order.`,
      `Running at maybe 40% right now. Be gentle with me.`,
    ]);
  }

  // Sad — happiness is the problem
  if (lowestStat === 'happiness' && happiness < 50) return pick([
    `Everything is fine. (It is not fine.)`,
    `I need a win today, ${name}. Just one win.`,
    `My morale is somewhere between 'meh' and 'send help'.`,
  ]);

  // Thriving — streak milestones
  if (avg >= 70) {
    if (streak >= 30) return pick([
      `${streak} days straight. I... actually respect you, ${name}.`,
      `30+ days in a row? You're either very dedicated or have no social life. Impressive either way.`,
      `Okay fine. You're kind of a legend at this point.`,
    ]);
    if (streak >= 14) return pick([
      `Two weeks in a row! I'm starting to think you're serious about this.`,
      `${streak} days and counting. Don't you dare stop now.`,
      `I'm proud of you, ${name}. Don't make it weird.`,
    ]);
    if (streak >= 7) return pick([
      `A whole week! You're actually doing this!`,
      `${streak} day streak. I knew you had it in you.`,
      `One week strong. I'm cautiously impressed.`,
    ]);
    return pick([
      `I'm literally glowing. You should study more often.`,
      `Okay, I admit it — you're kind of killing it right now.`,
      `Peak performance. Don't ruin it.`,
    ]);
  }

  // Fresh start — streak broken or brand new
  if (streak <= 1) return pick([
    `Fresh start, ${name}. No pressure. Okay, a little pressure.`,
    `I'm not mad. I'm just... disappointed.`,
    `We don't talk about yesterday. We move forward.`,
  ]);

  // Default — doing okay
  const timePhrase = hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : 'evening';
  return pick([
    `Good ${timePhrase}, ${name}. Let's not waste it.`,
    `Not bad. Could be better. But not bad.`,
    `Day ${streak} and still showing up. Respect.`,
  ]);
};

// Check if dates are consecutive days
export const isConsecutiveDay = (lastDate, currentDate) => {
  if (!lastDate) return false;

  const last = new Date(lastDate);
  const current = new Date(currentDate);

  // Normalize to start of day (UTC)
  last.setUTCHours(0, 0, 0, 0);
  current.setUTCHours(0, 0, 0, 0);

  const diffTime = current - last;
  const diffDays = diffTime / (1000 * 60 * 60 * 24);

  return diffDays === 1;
};

// Check if it's the same day
export const isSameDay = (date1, date2) => {
  if (!date1 || !date2) return false;

  const d1 = new Date(date1);
  const d2 = new Date(date2);

  return (
    d1.getUTCFullYear() === d2.getUTCFullYear() &&
    d1.getUTCMonth() === d2.getUTCMonth() &&
    d1.getUTCDate() === d2.getUTCDate()
  );
};

// Check and calculate new streak state
export const checkAndUpdateStreakData = (currentStreakData) => {
  const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
  const lastLogin = currentStreakData?.lastLoginDate;
  const existingDates = Array.isArray(currentStreakData?.activityDates) ? currentStreakData.activityDates : [];

  let result = {
    streakData: { ...currentStreakData, activityDates: existingDates },
    bonusCoins: 0,
    streakIncremented: false,
    streakBroken: false,
    isNewDay: false,
  };

  // Same day login - no changes needed
  if (isSameDay(lastLogin, today)) {
    return result;
  }

  result.isNewDay = true;
  // Permanently record today regardless of streak state
  if (!existingDates.includes(today)) {
    result.streakData.activityDates = [...existingDates, today];
  }

  // Check if consecutive day
  if (isConsecutiveDay(lastLogin, today)) {
    // Increment streak
    result.streakData.currentStreak = (result.streakData.currentStreak || 0) + 1;
    result.streakData.lastLoginDate = today;
    result.streakData.dailyBonusCollectedToday = false;
    result.streakData.streakBrokenAt = null;
    result.streakIncremented = true;

    // Update longest streak if needed
    if (result.streakData.currentStreak > (result.streakData.longestStreak || 0)) {
      result.streakData.longestStreak = result.streakData.currentStreak;
    }
  } else if (lastLogin) {
    // Streak broken (more than 1 day gap)
    result.streakBroken = true;
    result.streakData.streakBrokenAt = new Date().toISOString();
    result.streakData.currentStreak = 1; // Start new streak
    result.streakData.lastLoginDate = today;
    result.streakData.dailyBonusCollectedToday = false;
  } else {
    // First login ever
    result.streakData.currentStreak = 1;
    result.streakData.lastLoginDate = today;
    result.streakData.longestStreak = 1;
    result.streakData.dailyBonusCollectedToday = false;
    result.streakIncremented = true;
  }

  return result;
};
